import { useId, useState } from "react";
import { ThumbsDown, ThumbsUp, X } from "lucide-react";
import {
  NEGATIVE_IMPACT_OPTIONS,
  POSITIVE_IMPACT_OPTIONS,
} from "~/components/assistant/impact-options";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { FeedbackCreate, TurnFeedback as TurnFeedbackValue } from "~/services/turns";

type Thumb = "up" | "down";

interface TurnFeedbackProps {
  feedback: TurnFeedbackValue | FeedbackCreate | null;
  isSubmitting: boolean;
  onSubmit: (value: 0 | 1, text: string | null, impact: number | null) => Promise<void>;
  onClear: () => void;
}

// Thumbs up / down for an assistant turn. On click, opens a popover with an
// optional impact dropdown and a free-text details box; Submit posts the
// feedback. Clicking the already-selected thumb re-opens the popover with
// the existing text/impact so the user can edit. The X clears the feedback.
export const TurnFeedback = ({ feedback, isSubmitting, onSubmit, onClear }: TurnFeedbackProps) => {
  const [open, setOpen] = useState<Thumb | null>(null);
  const [text, setText] = useState("");
  const [impact, setImpact] = useState<number | null>(null);
  const impactId = useId();
  const detailsId = useId();

  const currentValue = feedback?.feedback_value ?? null;

  const handleThumbClick = (thumb: Thumb) => {
    if (open === thumb) {
      setOpen(null);
      return;
    }
    const sameDirection =
      (thumb === "up" && currentValue === 1) || (thumb === "down" && currentValue === 0);
    setOpen(thumb);
    setText(sameDirection ? feedback?.text || "" : "");
    setImpact(sameDirection ? (feedback?.impact ?? null) : null);
  };

  const handleSubmit = async () => {
    if (isSubmitting || !open) return;
    const value = open === "up" ? 1 : 0;
    await onSubmit(value as 0 | 1, text.trim() || null, impact);
    setOpen(null);
    setText("");
    setImpact(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const popoverBody = (thumb: Thumb) => {
    const impactOptions = thumb === "up" ? POSITIVE_IMPACT_OPTIONS : NEGATIVE_IMPACT_OPTIONS;
    return (
      <div className="flex flex-col gap-3">
        <div className="text-sm font-medium">Feedback</div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={impactId} className="text-xs text-muted-foreground">
            Impact: (optional)
          </Label>
          <Select
            value={impact != null ? String(impact) : "none"}
            onValueChange={(v) => setImpact(v === "none" ? null : Number(v))}
            disabled={isSubmitting}
          >
            <SelectTrigger id={impactId} className="w-full">
              <SelectValue placeholder="Select impact level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {impactOptions.map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>
                  {o.label}: {o.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={detailsId} className="text-xs text-muted-foreground">
            Provide Details: (optional)
          </Label>
          <Textarea
            id={detailsId}
            placeholder={
              thumb === "up"
                ? "Tell us what you liked about this response"
                : "Tell us what you didn't like about this response"
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[80px]"
            disabled={isSubmitting}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Your feedback will make this entire conversation visible to administrators.
        </p>
        <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
          Submit
        </Button>
      </div>
    );
  };

  const thumbButton = (thumb: Thumb) => {
    const isActive =
      (thumb === "up" && currentValue === 1) || (thumb === "down" && currentValue === 0);
    const Icon = thumb === "up" ? ThumbsUp : ThumbsDown;
    return (
      <Popover open={open === thumb} onOpenChange={(o) => !o && setOpen(null)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={() => handleThumbClick(thumb)}
                disabled={isSubmitting}
                aria-label={thumb === "up" ? "Helpful" : "Not helpful"}
                aria-pressed={isActive}
                className={cn(
                  "cursor-pointer rounded p-1 text-muted-foreground",
                  "transition-[color,background-color,transform] duration-150 ease-out",
                  "hover:bg-muted active:scale-90 disabled:opacity-50",
                  isActive && "text-foreground"
                )}
              >
                <Icon
                  className={cn(
                    "size-4 transition-transform duration-200 ease-out",
                    isActive && "scale-110"
                  )}
                  fill={isActive ? "currentColor" : "none"}
                />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{thumb === "up" ? "Helpful" : "Not helpful"}</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
          {popoverBody(thumb)}
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="flex items-center gap-1">
      {thumbButton("up")}
      {thumbButton("down")}
      {feedback && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClear}
              disabled={isSubmitting}
              aria-label="Clear feedback"
              className={cn(
                "cursor-pointer rounded p-1 text-muted-foreground transition-colors",
                "hover:bg-muted disabled:opacity-50"
              )}
            >
              <X className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Clear feedback</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
